import { loadRenderer, type Renderer } from '@renderer';
import type { Region } from './selector';

const TIMEOUT = 10_000;

function withTimeout<T>(p: Promise<T>, ms: number): Promise<T> {
  return new Promise((resolve, reject) => {
    const t = setTimeout(() => reject(new Error('timeout')), ms);
    p.then((v) => (clearTimeout(t), resolve(v)), (e) => (clearTimeout(t), reject(e)));
  });
}

export function renderer(): Promise<Renderer> {
  return withTimeout(loadRenderer(), TIMEOUT);
}

/**
 * Maps a viewport region (CSS px) onto the rendered body canvas (device px).
 * html-to-image draws the body from its own top-left, so page coordinates are
 * `viewport + scroll - body offset`.
 */
export function cropRect(region: Region, body: { left: number; top: number }, scale: number, canvas: { width: number; height: number }) {
  const sx = Math.max(0, Math.round((region.x - body.left) * scale));
  const sy = Math.max(0, Math.round((region.y - body.top) * scale));
  const sw = Math.min(canvas.width - sx, Math.round(region.width * scale));
  const sh = Math.min(canvas.height - sy, Math.round(region.height * scale));
  return { sx, sy, sw: Math.max(1, sw), sh: Math.max(1, sh) };
}

/**
 * DOM render (html-to-image) with the widget hidden, optionally cropped to a region; falls back
 * to getDisplayMedia (uncropped — the captured surface is the whole tab). Throws if both fail.
 */
export async function captureScreen(host: HTMLElement, region: Region | 'full' = 'full'): Promise<Blob> {
  host.style.display = 'none';
  try {
    try {
      const { toCanvas } = await renderer();
      const scale = Math.min(window.devicePixelRatio || 1, 2);
      // Render at least the viewport size: short pages have a body smaller than the viewport,
      // and a region selected below the content must still be croppable.
      const doc = document.documentElement;
      const full = await withTimeout(
        toCanvas(document.body, {
          cacheBust: true,
          pixelRatio: scale,
          width: Math.max(doc.scrollWidth, window.innerWidth),
          height: Math.max(doc.scrollHeight, window.innerHeight),
          filter: (node: Node) => !(node instanceof Element && node.id === 'fetrace-root'),
          backgroundColor: pageBackground(),
        }),
        TIMEOUT,
      );
      if (!full || full.width === 0) throw new Error('empty render');

      let out: HTMLCanvasElement = full;
      if (region !== 'full') {
        const bodyRect = document.body.getBoundingClientRect();
        const { sx, sy, sw, sh } = cropRect(region, bodyRect, scale, full);
        out = document.createElement('canvas');
        out.width = sw;
        out.height = sh;
        out.getContext('2d')!.drawImage(full, sx, sy, sw, sh, 0, 0, sw, sh);
      }
      const blob = await new Promise<Blob | null>((r) => out.toBlob(r, 'image/png'));
      if (blob && blob.size > 0) return blob;
      throw new Error('empty blob');
    } catch {
      return await captureDisplay();
    }
  } finally {
    host.style.display = '';
  }
}

/** Pages rarely paint the body; fall back to <html>, then white, so captures are not transparent. */
export function pageBackground(): string {
  const transparent = (c: string) => !c || c === 'transparent' || /^rgba\(\s*\d+,\s*\d+,\s*\d+,\s*0\s*\)$/.test(c);
  const body = getComputedStyle(document.body).backgroundColor;
  if (!transparent(body)) return body;
  const html = getComputedStyle(document.documentElement).backgroundColor;
  return transparent(html) ? '#ffffff' : html;
}

async function captureDisplay(): Promise<Blob> {
  const md = navigator.mediaDevices;
  if (!md || typeof md.getDisplayMedia !== 'function') throw new Error('getDisplayMedia unavailable');
  const stream = await md.getDisplayMedia({ video: { displaySurface: 'browser' } as MediaTrackConstraints, audio: false });
  try {
    const video = document.createElement('video');
    video.srcObject = stream;
    video.muted = true;
    await video.play();
    await new Promise((r) => setTimeout(r, 300));
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d')!.drawImage(video, 0, 0);
    const blob = await new Promise<Blob | null>((r) => canvas.toBlob(r, 'image/png'));
    if (!blob) throw new Error('canvas empty');
    return blob;
  } finally {
    stream.getTracks().forEach((t) => t.stop());
  }
}

/**
 * Upload normalisation: cap the longest edge (default 2200 px) without visible quality loss.
 * - Already within limits → bytes are returned untouched (no re-encode, no loss).
 * - PNG/GIF/WEBP-lossless-ish sources stay PNG (lossless); JPEG stays JPEG at q=0.95.
 * - Downscale in halving steps with high-quality smoothing (avoids the blurry single-step resample).
 */
export async function normalizeImage(blob: Blob, maxEdge = 2200, maxBytes = 5 * 1024 * 1024): Promise<Blob> {
  if (!blob.type.startsWith('image/') || blob.type === 'image/gif') return blob;
  const url = URL.createObjectURL(blob);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const i = new Image();
      i.onload = () => resolve(i);
      i.onerror = () => reject(new Error('image'));
      i.src = url;
    });
    const w = img.naturalWidth;
    const hgt = img.naturalHeight;
    if (!w || !hgt) return blob;
    if (Math.max(w, hgt) <= maxEdge && blob.size <= maxBytes) return blob;
    const type = blob.type === 'image/jpeg' ? 'image/jpeg' : 'image/png';
    let scale = Math.min(1, maxEdge / Math.max(w, hgt));
    let out = await resample(img, scale, type);
    // Still over the byte budget (huge PNG screenshots): step down until it fits or we hit 1000 px.
    while (out.size > maxBytes && Math.max(w, hgt) * scale > 1000) {
      scale *= 0.8;
      out = await resample(img, scale, type);
    }
    return out;
  } catch {
    return blob;
  } finally {
    URL.revokeObjectURL(url);
  }
}

async function resample(img: HTMLImageElement, scale: number, type: string): Promise<Blob> {
  let cur: HTMLCanvasElement | HTMLImageElement = img;
  let cw = img.naturalWidth;
  let ch = img.naturalHeight;
  const tw = Math.max(1, Math.round(cw * scale));
  const th = Math.max(1, Math.round(ch * scale));
  // halve repeatedly, final step lands on the exact target
  while (cw / 2 > tw) {
    cur = draw(cur, Math.round(cw / 2), Math.round(ch / 2));
    cw = Math.round(cw / 2);
    ch = Math.round(ch / 2);
  }
  const final = draw(cur, tw, th);
  return new Promise((resolve, reject) => final.toBlob((b) => (b ? resolve(b) : reject(new Error('toBlob failed'))), type, 0.95));
}

function draw(src: HTMLCanvasElement | HTMLImageElement, w: number, h: number): HTMLCanvasElement {
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  const ctx = c.getContext('2d')!;
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(src, 0, 0, w, h);
  return c;
}
